// Pixels e analytics (Meta, TikTok, Google, Pinterest, LinkedIn, X, Snapchat, Clarity).
// As tags só são carregadas depois do consentimento (quando exigido) e a partir da configuração do admin.
import { api } from './api';
import { visitFromTrack } from './visits';

const CONSENT_KEY = 'pp_consent';
let config = null;
let started = false;
const pending = []; // eventos aguardando o consentimento / carregamento das tags
const early = []; // eventos disparados antes da configuração chegar do servidor

const safe = (fn) => { try { fn(); } catch (e) { /* uma tag com erro não pode quebrar o site */ } };
const w = () => window;

export const getConsent = () => { try { return localStorage.getItem(CONSENT_KEY); } catch { return null; } };
export const setConsent = (v) => {
  try { localStorage.setItem(CONSENT_KEY, v); } catch { /* ignora */ }
  safe(() => updateGoogleConsent(v === 'granted')); // eslint-disable-line no-use-before-define
};
export const clearConsent = () => { try { localStorage.removeItem(CONSENT_KEY); } catch { /* ignora */ } };

export const hasAnyTracker = (c) => Boolean(c && (
  c.meta_pixel_id || c.tiktok_pixel_id || c.ga4_id || c.google_ads_id || c.gtm_id || c.pinterest_tag_id
  || c.linkedin_partner_id || c.twitter_pixel_id || c.snapchat_pixel_id || c.clarity_id || c.custom_head_script
));

export async function loadTrackingConfig() {
  if (config) return config;
  try {
    const { data } = await api.get('/tracking-config');
    config = data;
  } catch {
    config = {};
  }
  early.splice(0).forEach(([n, p]) => track(n, p)); // eslint-disable-line no-use-before-define
  return config;
}

function loadScript(src, attrs = {}) {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

/** Executa um trecho HTML com <script> (innerHTML sozinho não executa scripts). */
function injectHtml(html) {
  const box = document.createElement('div');
  box.innerHTML = html;
  box.querySelectorAll('script').forEach((old) => {
    const s = document.createElement('script');
    [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
    s.text = old.text;
    document.head.appendChild(s);
  });
}

function initMeta(id) {
  const win = w();
  if (win.fbq) return;
  const n = (win.fbq = function fbq(...args) { if (n.callMethod) n.callMethod(...args); else n.queue.push(args); });
  if (!win._fbq) win._fbq = n;
  n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
  loadScript('https://connect.facebook.net/en_US/fbevents.js');
  win.fbq('init', id);
}

function initTikTok(id) {
  const win = w();
  const t = 'ttq';
  win.TiktokAnalyticsObject = t;
  const ttq = (win[t] = win[t] || []);
  ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
  ttq.setAndDefer = (o, e) => { o[e] = (...a) => o.push([e, ...a]); };
  ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
  ttq.instance = (name) => { const inst = ttq._i[name] || []; ttq.methods.forEach((m) => ttq.setAndDefer(inst, m)); return inst; };
  ttq.load = (e, n) => {
    const src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
    ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = src;
    ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
    ttq._o = ttq._o || {}; ttq._o[e] = n || {};
    loadScript(`${src}?sdkid=${e}&lib=${t}`);
  };
  ttq.load(id);
}

// ---- Google: Modo de consentimento (v2, avançado) ----
// A tag do Google carrega já na abertura com tudo "negado": sem cookies e sem identificar a pessoa, ela só manda sinais
// anônimos de visita e de compra, e o Google estima as vendas de quem recusou. Ao aceitar, o consentimento é atualizado.
// As demais tags (Meta, TikTok etc.) continuam só depois do "Aceitar".
let googleStarted = false;
const consentState = (granted) => {
  const v = granted ? 'granted' : 'denied';
  return { ad_storage: v, ad_user_data: v, ad_personalization: v, analytics_storage: v };
};

function updateGoogleConsent(granted) {
  const win = w();
  if (googleStarted && win.gtag) win.gtag('consent', 'update', consentState(granted));
}

function startGoogle(c) {
  if (googleStarted || !(c.ga4_id || c.google_ads_id)) return;
  googleStarted = true;
  const win = w();
  win.dataLayer = win.dataLayer || [];
  win.gtag = win.gtag || function gtag() { win.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
  win.gtag('consent', 'default', { ...consentState(false), wait_for_update: 500 });
  if (c.require_consent === false || getConsent() === 'granted') win.gtag('consent', 'update', consentState(true));
  initGoogle(c);
}

function initGoogle(c) {
  const win = w();
  win.dataLayer = win.dataLayer || [];
  win.gtag = win.gtag || function gtag() { win.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
  const first = c.ga4_id || c.google_ads_id;
  if (!first) return;
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(first)}`);
  win.gtag('js', new Date());
  if (c.ga4_id) win.gtag('config', c.ga4_id, { send_page_view: false });
  if (c.google_ads_id) win.gtag('config', c.google_ads_id);
}

function initGtm(id) {
  const win = w();
  win.dataLayer = win.dataLayer || [];
  win.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  loadScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`);
}

function initPinterest(id) {
  const win = w();
  if (!win.pintrk) {
    win.pintrk = (...a) => win.pintrk.queue.push(a);
    win.pintrk.queue = []; win.pintrk.version = '3.0';
    loadScript('https://s.pinimg.com/ct/core.js');
  }
  win.pintrk('load', id);
}

function initLinkedIn(id) {
  const win = w();
  win._linkedin_data_partner_ids = win._linkedin_data_partner_ids || [];
  win._linkedin_data_partner_ids.push(id);
  loadScript('https://snap.licdn.com/li.lms-analytics/insight.min.js');
}

function initTwitter(id) {
  const win = w();
  if (!win.twq) {
    const s = (win.twq = (...a) => (s.exe ? s.exe(...a) : s.queue.push(a)));
    s.version = '1.1'; s.queue = [];
    loadScript('https://static.ads-twitter.com/uwt.js');
  }
  win.twq('config', id);
}

function initSnapchat(id) {
  const win = w();
  if (!win.snaptr) {
    const a = (win.snaptr = (...args) => (a.handleRequest ? a.handleRequest(...args) : a.queue.push(args)));
    a.queue = [];
    loadScript('https://sc-static.net/scevent.min.js');
  }
  win.snaptr('init', id, {});
}

function initClarity(id) {
  const win = w();
  win.clarity = win.clarity || function clarity(...a) { (win.clarity.q = win.clarity.q || []).push(a); };
  loadScript(`https://www.clarity.ms/tag/${encodeURIComponent(id)}`);
}

/** Carrega todas as tags configuradas (uma única vez). */
function startTrackers(c) {
  if (started) return;
  started = true;
  if (c.meta_pixel_id) safe(() => initMeta(c.meta_pixel_id));
  if (c.tiktok_pixel_id) safe(() => initTikTok(c.tiktok_pixel_id));
  if (c.ga4_id || c.google_ads_id) safe(() => { startGoogle(c); updateGoogleConsent(true); });
  if (c.gtm_id) safe(() => initGtm(c.gtm_id));
  if (c.pinterest_tag_id) safe(() => initPinterest(c.pinterest_tag_id));
  if (c.linkedin_partner_id) safe(() => initLinkedIn(c.linkedin_partner_id));
  if (c.twitter_pixel_id) safe(() => initTwitter(c.twitter_pixel_id));
  if (c.snapchat_pixel_id) safe(() => initSnapchat(c.snapchat_pixel_id));
  if (c.clarity_id) safe(() => initClarity(c.clarity_id));
  if (c.custom_head_script) safe(() => injectHtml(c.custom_head_script));
  // eventos que esperaram o aceite: o Google já recebeu cada um (de forma anônima), então ele fica de fora aqui
  pending.splice(0).forEach(([name, params]) => dispatch(name, params, { skipGoogle: true }));
}

/** Inicia as tags se o consentimento já foi dado (ou não é exigido). */
export function maybeStart(c = config) {
  if (!c || !hasAnyTracker(c)) return false;
  safe(() => startGoogle(c)); // Google em modo de consentimento desde a abertura
  if (c.require_consent === false || getConsent() === 'granted') {
    startTrackers(c);
    return true;
  }
  return false;
}

export const isRunning = () => started;

// ---- Eventos ----
// Nomes internos: page_view, add_to_cart, begin_checkout, lead, add_payment_info, purchase
// params: { value, currency, content_id, content_name, quantity, transaction_id }
function dispatchGoogle(name, p = {}) {
  const c = config || {};
  const win = w();
  const currency = p.currency || 'BRL';
  const item = p.content_id ? [{ id: p.content_id, name: p.content_name, quantity: p.quantity || 1, price: p.value && p.quantity ? p.value / p.quantity : p.value }] : undefined;
  if (!((c.ga4_id || c.google_ads_id) && win.gtag)) return;
  if (name === 'page_view') {
    if (c.ga4_id) safe(() => win.gtag('event', 'page_view', { page_location: window.location.href, page_path: window.location.pathname, page_title: document.title }));
  } else if (c.ga4_id) {
    const map = { add_to_cart: 'add_to_cart', begin_checkout: 'begin_checkout', lead: 'generate_lead', add_payment_info: 'add_payment_info', purchase: 'purchase', contact: 'contact' };
    safe(() => win.gtag('event', map[name] || name, { value: p.value, currency, transaction_id: p.transaction_id, items: item }));
  }
  if (name === 'purchase' && c.google_ads_id && c.google_ads_purchase_label) {
    safe(() => win.gtag('event', 'conversion', { send_to: `${c.google_ads_id}/${c.google_ads_purchase_label}`, value: p.value, currency, transaction_id: p.transaction_id }));
  }
}

function dispatch(name, p = {}, opts = {}) {
  const c = config || {};
  const win = w();
  const currency = p.currency || 'BRL';
  const item = p.content_id ? [{ id: p.content_id, name: p.content_name, quantity: p.quantity || 1, price: p.value && p.quantity ? p.value / p.quantity : p.value }] : undefined;

  if (c.meta_pixel_id && win.fbq) {
    const map = { page_view: ['PageView'], add_to_cart: ['AddToCart'], begin_checkout: ['InitiateCheckout'], lead: ['Lead'], add_payment_info: ['AddPaymentInfo'], purchase: ['Purchase'], contact: ['Contact'] };
    const [ev] = map[name] || [];
    if (ev && name !== 'page_view') {
      safe(() => win.fbq('track', ev, { value: p.value, currency, content_ids: p.content_id ? [p.content_id] : undefined, content_name: p.content_name, content_type: 'product', num_items: p.quantity }, p.transaction_id ? { eventID: p.transaction_id } : undefined));
    } else if (name === 'page_view') safe(() => win.fbq('track', 'PageView'));
  }

  if (c.tiktok_pixel_id && win.ttq) {
    const map = { add_to_cart: 'AddToCart', begin_checkout: 'InitiateCheckout', lead: 'SubmitForm', add_payment_info: 'AddPaymentInfo', purchase: 'CompletePayment', contact: 'Contact' };
    if (name === 'page_view') safe(() => win.ttq.page());
    else if (map[name]) safe(() => win.ttq.track(map[name], { value: p.value, currency, content_id: p.content_id, content_name: p.content_name, content_type: 'product', quantity: p.quantity }));
  }

  if (!opts.skipGoogle) dispatchGoogle(name, p);

  if (c.gtm_id && win.dataLayer) {
    safe(() => win.dataLayer.push({ event: name, ecommerce: { value: p.value, currency, transaction_id: p.transaction_id, items: item }, lead_source: p.content_name }));
  }

  if (c.pinterest_tag_id && win.pintrk) {
    const map = { add_to_cart: 'addtocart', begin_checkout: 'checkout', lead: 'lead', purchase: 'checkout' };
    if (name === 'page_view') safe(() => win.pintrk('page'));
    else if (map[name]) safe(() => win.pintrk('track', map[name], { value: p.value, currency, order_id: p.transaction_id, product_name: p.content_name }));
  }

  if (c.twitter_pixel_id && win.twq) {
    if (name === 'page_view') safe(() => win.twq('track', 'PageView'));
    else if (name === 'purchase') safe(() => win.twq('track', 'Purchase', { value: p.value, currency, conversion_id: p.transaction_id }));
    else if (name === 'lead') safe(() => win.twq('track', 'Lead'));
    else if (name === 'begin_checkout') safe(() => win.twq('track', 'InitiateCheckout'));
    else if (name === 'add_to_cart') safe(() => win.twq('track', 'AddToCart', { value: p.value, currency }));
  }

  if (c.snapchat_pixel_id && win.snaptr) {
    const map = { page_view: 'PAGE_VIEW', add_to_cart: 'ADD_CART', begin_checkout: 'START_CHECKOUT', lead: 'SIGN_UP', add_payment_info: 'ADD_BILLING', purchase: 'PURCHASE' };
    if (map[name]) safe(() => win.snaptr('track', map[name], name === 'page_view' ? undefined : { price: p.value, currency, transaction_id: p.transaction_id, item_ids: p.content_id ? [p.content_id] : undefined }));
  }

}

/** Registra um evento; se as tags ainda não carregaram (aguardando consentimento), ignora ou enfileira. */
export function track(name, params) {
  visitFromTrack(name, params); // contagem própria de visitas (anônima, não depende de consentimento nem de pixel)
  if (!config) {
    early.push([name, params]);
    if (early.length > 30) early.shift();
    return;
  }
  if (!hasAnyTracker(config)) return;
  if (started) { dispatch(name, params); return; }
  safe(() => { if (googleStarted) dispatchGoogle(name, params); }); // Google: sempre, de forma anônima se não houve aceite
  if (getConsent() !== 'denied') {
    pending.push([name, params]);
    if (pending.length > 30) pending.shift();
  }
}

// Clique no WhatsApp (avisado por src/lib/visits.js): vira o evento "contact" nos pixels configurados.
if (typeof window !== 'undefined') window.addEventListener('pp:contact', () => track('contact'));
